import { useState } from "react";
import { ImagePlus, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg } from "@/lib/api";

const compress = (file, max = 1600) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const s = Math.min(1, max / Math.max(image.width, image.height));
      const c = document.createElement("canvas");
      c.width = Math.round(image.width * s);
      c.height = Math.round(image.height * s);
      c.getContext("2d").drawImage(image, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b || file), "image/jpeg", 0.85);
    };
    image.onerror = () => resolve(file);
    image.src = URL.createObjectURL(file);
  });

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", await compress(file), "image.jpg");
  return (await api.post("/uploads", fd)).data.url;
}

export function ImageUploader({ value, onChange, max = 5, testid = "image-uploader" }) {
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    const files = [...e.target.files].slice(0, max - value.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try {
        urls.push(await uploadImage(f));
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    onChange([...value, ...urls]);
    setBusy(false);
  };
  const move = (i, d) => {
    const n = [...value];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    onChange(n);
  };

  return (
    <div data-testid={testid} className="grid grid-cols-3 sm:grid-cols-5 gap-3">
      {value.map((u, i) => (
        <div key={u} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group">
          <img src={img(u)} alt="" className="w-full h-full object-cover" />
          {i === 0 && <span className="absolute top-1 left-1 text-[10px] font-bold bg-red-500 text-white px-1.5 rounded">COVER</span>}
          <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 p-1">
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-white disabled:opacity-30" data-testid={`image-left-${i}`}><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-white" data-testid={`image-remove-${i}`}><X className="w-4 h-4" /></button>
            <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)} className="text-white disabled:opacity-30" data-testid={`image-right-${i}`}><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      {value.length < max && (
        <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-red-400 text-slate-500">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <div className="text-center text-xs"><ImagePlus className="w-6 h-6 mx-auto mb-1" />Add photo<br />({value.length}/{max})</div>}
          <input data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={add} />
        </label>
      )}
    </div>
  );
}
